import { Alert, Stack } from '@mui/material';
import { t } from 'i18next';
import { FormEventHandler, useCallback, useEffect, useState } from 'react';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { PasswordInput } from '../../common/components/inputs/PasswordInput';
import { getErrorMessage } from '../../common/errors/errors';
import { OrganizationUsersRepository } from '../../organization/users/services/OrganizationUsersRepository';
import { ButtonBox, PrimaryButton, TertiaryButton } from '../components/Buttons';

type Props = {
  username: string;
  onClosed: (changed: boolean) => void;
};

type Inputs = {
  current: string | null;
  new: string | null;
  confirm: string | null;
};

export const ChangePasswordFlow = ({ username, onClosed }: Props) => {
  const [userService] = useState(new OrganizationUsersRepository());
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [passwords, setPasswords] = useState<Inputs>({ current: null, new: null, confirm: null });

  useEffect(() => {
    return () => {
      userService.cancelAllRequests();
    };
  }, [userService]);

  const onPasswordChange = useCallback((value: string | null, target: keyof Inputs) => {
    setPasswords((prev) => ({ ...prev, [target]: value }));
  }, []);

  const { current: currentPassword, new: newPassword, confirm: confirmPassword } = passwords;

  const canSubmit = Boolean(!loading && currentPassword && newPassword && confirmPassword && confirmPassword === newPassword);

  const onSubmit: FormEventHandler = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword) return;
    try {
      setLoading(true);
      setError(null);
      await userService.changePassword({ currentPassword, newPassword });
      onClosed(true);
    } catch (e: unknown) {
      setError(getErrorMessage(e));
      setLoading(false);
    }
  };

  return (
    <Dialog title={t('changeOwnPassword.title')} subtitle={t('changeOwnPassword.subtitle')} onDismiss={() => onClosed(false)}>
      <DialogBody>
        <Stack component="form" spacing={1.5} onSubmit={onSubmit}>
          {error && <Alert severity="error">{error}</Alert>}
          {/* Add an invisible username input to enable browser password update suggestions */}
          <input hidden readOnly type="text" value={username} name="username" autoComplete="username email" />
          <PasswordInput
            autofocus
            name="current-password"
            label={t('changeOwnPassword.current.input')}
            focusedLabel={t('changeOwnPassword.current.focused')}
            disabled={loading}
            onChange={(value) => onPasswordChange(value, 'current')}
          />
          <PasswordInput
            name="new-password"
            label={t('changeOwnPassword.new.input')}
            focusedLabel={t('changeOwnPassword.new.focused')}
            disabled={loading}
            onChange={(value) => onPasswordChange(value, 'new')}
          />
          <PasswordInput
            name="confirm-password"
            label={t('changeOwnPassword.confirm.input')}
            focusedLabel={t('changeOwnPassword.confirm.focused')}
            disabled={loading}
            compareTo={passwords.new}
            onChange={(value) => onPasswordChange(value, 'confirm')}
          />
          <ButtonBox>
            <TertiaryButton disabled={loading} onClick={() => onClosed(false)}>
              {t('changeOwnPassword.back')}
            </TertiaryButton>
            <PrimaryButton disabled={!canSubmit} type="submit">
              {t('changeOwnPassword.submit')}
            </PrimaryButton>
          </ButtonBox>
        </Stack>
      </DialogBody>
    </Dialog>
  );
};
